import {
  Image,
  Link,
  ListItem,
  SimpleGrid,
  Stack,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import generateCdnUrl from "@raiden/library/libraries/utils/generateCdnUrl";

/**
 * @typedef {Object} TimelineItem
 * @property {string} dayLabel
 * @property {string} title
 * @property {import("react").ReactNode} children
 */

/**
 * @param {object} params
 * @param {import("react-intl").IntlShape} params.intl
 * @returns {TimelineItem[]}
 */
export function getTimelineData({ intl }) {
  const plans = [
    {
      img: "plan1.png",
      label: intl.formatMessage({ defaultMessage: "Exemple 1 de plan" }),
      link: generateCdnUrl({
        id: "@cdn/plan-1",
      }),
    },
    {
      img: "plan2.png",
      label: intl.formatMessage({ defaultMessage: "Exemple 1 de coupe" }),
      link: generateCdnUrl({
        id: "@cdn/coupe-1",
      }),
    },
    {
      img: "plan3.png",
      label: intl.formatMessage({ defaultMessage: "Exemple 2 de plan" }),
      link: generateCdnUrl({
        id: "@cdn/plan-2-2",
      }),
    },
    {
      img: "plan4.png",
      label: intl.formatMessage({ defaultMessage: "Exemple 2 de coupe" }),
      link: generateCdnUrl({
        id: "@cdn/coupe-2",
      }),
    },
  ];

  return [
    {
      dayLabel: intl.formatMessage({ defaultMessage: "Jour J" }),
      title: intl.formatMessage({ defaultMessage: "Survenance du sinistre." }),
      children: (
        <Text>
          {intl.formatMessage({ defaultMessage: "Survenance du sinistre" })}
        </Text>
      ),
    },
    {
      dayLabel: intl.formatMessage({ defaultMessage: "J+1" }),
      title: intl.formatMessage({ defaultMessage: "Premiers contacts" }),
      children: (
        <Stack spacing=".85rem">
          <UnorderedList>
            <ListItem fontSize="1.125rem" color="brandPrimary.700">
              {intl.formatMessage({
                defaultMessage:
                  "Mise en place de mesures conservatoires (bâchages, étaiement, ...) en concertations avec toutes les parties concernées",
              })}
            </ListItem>

            <ListItem fontSize="1.125rem" color="brandPrimary.700">
              {intl.formatMessage({
                defaultMessage:
                  "Analyse de la police d’assurance (vérification du nombre de pièces et de la  surface développée, du capital garanti sur le mobilier, des garanties annexes, …)",
              })}
            </ListItem>
          </UnorderedList>

          <Text>
            {intl.formatMessage({
              defaultMessage:
                "Note : les frais et honoraires d’expertise sont calculés en fonction d’un taux de rémunération appliqué au montant des dommages garantis. ",
            })}
          </Text>

          <Text>
            {intl.formatMessage({
              defaultMessage:
                "Cette garantie supplémentaire peut être incluse dans les contrats d’assurance et doit faire l’objet d’une vérification par notre cabinet.",
            })}
          </Text>

          <UnorderedList pl="16px">
            <ListItem fontSize="1.125rem" color="brandPrimary.700">
              {intl.formatMessage({
                defaultMessage:
                  "Demande d’un acompte pour les premiers débours (frais de relogement, achat de mobilier, de vêtements de première nécessité, avance pour les premières mesures de sauvegarde, engagement de frais supplémentaires pour limiter la perte d'exploitation, ...).",
              })}
            </ListItem>
          </UnorderedList>
        </Stack>
      ),
    },
    {
      dayLabel: intl.formatMessage({ defaultMessage: "J+5" }),
      title: intl.formatMessage({
        defaultMessage: "Intervention sur le lieu du sinistre",
      }),
      children: (
        <Text>
          {intl.formatMessage(
            {
              defaultMessage:
                "Relevé technique : prise de mesures précises et établissement d’un <b>descriptif détaillé des dommages</b> au bâtiment par notre expert, en  collaboration avec l’assuré pour l’inventaire du mobilier.",
            },
            {
              b: (chunks) => <strong>{chunks}</strong>,
            },
          )}
        </Text>
      ),
    },
    {
      dayLabel: intl.formatMessage({ defaultMessage: "J+30" }),
      title: intl.formatMessage({
        defaultMessage: "Plans",
      }),
      children: (
        <Stack spacing=".25rem">
          <Text>
            {intl.formatMessage({
              defaultMessage:
                "Établissement des plans du bâtiment détruit, si nécessaire, par notre service architecture. (cliquez sur les plans pour les agrandir)",
            })}
          </Text>

          <SimpleGrid
            columns={{ base: 1, sm: 2, md: 3, lg: 4 }}
            spacing="1.5rem">
            {plans.map((plan, index) => (
              <Stack
                spacing="1rem"
                key={index}
                alignItems="center"
                justifyContent="center">
                <Link href={plan.link} target="_blank">
                  <Image
                    src={generateFrontPath({
                      id: "@front.internal-assets",
                      parameters: { filePath: `plans/${plan.img}` },
                      includeBasePath: true,
                    })}
                    alt="Logo"
                    minW="200px"
                    minH="181px"
                  />
                </Link>

                <Text>{plan.label}</Text>
              </Stack>
            ))}
          </SimpleGrid>
        </Stack>
      ),
    },
    {
      dayLabel: intl.formatMessage({ defaultMessage: "J+60" }),
      title: intl.formatMessage({
        defaultMessage: "État de pertes bâtiment",
      }),
      children: (
        <Text>
          {intl.formatMessage(
            {
              defaultMessage:
                "Réalisation d’un état descriptif détaillé par corps d’état.{br}Exemple d’un <link>relevé de charpente-couverture</link>",
            },
            {
              br: <br />,
              link: (chunks) => (
                <Link
                  textDecoration="underline"
                  href={generateCdnUrl({
                    id: "@cdn/etat-preparatoire",
                  })}
                  target="_blank">
                  {chunks}
                </Link>
              ),
            },
          )}
        </Text>
      ),
    },
    {
      dayLabel: intl.formatMessage({ defaultMessage: "J+60" }),
      title: intl.formatMessage({ defaultMessage: "État de pertes mobilier" }),
      children: (
        <Text>
          {intl.formatMessage({
            defaultMessage:
              "Réalisation d’un état détaillé mobilier détruit pièce par pièce.",
          })}
        </Text>
      ),
    },
    {
      dayLabel: intl.formatMessage({ defaultMessage: "J+60 +" }),
      title: intl.formatMessage({ defaultMessage: "Expertise contradictoire" }),
      children: (
        <Text>
          {intl.formatMessage({
            defaultMessage:
              "Négociation avec votre compagnie d’assurance et son expert sur le  montant des dommages, en explorant toutes les possibilités de votre  contrat d’assurance pour vous assurer une juste indemnisation de votre préjudice.",
          })}
        </Text>
      ),
    },
    {
      dayLabel: intl.formatMessage({ defaultMessage: "J+90" }),
      title: intl.formatMessage({ defaultMessage: "Avancement du dossier" }),
      children: (
        <Text>
          {intl.formatMessage({
            defaultMessage:
              "Sauf imprévu, présentation des résultats de nos démarches et de l’estimation fixée en accord avec l’expert de la compagnie d’assurance.",
          })}
        </Text>
      ),
    },
    {
      dayLabel: intl.formatMessage({ defaultMessage: "J+90 à 120" }),
      title: intl.formatMessage({ defaultMessage: "Clôture" }),
      children: (
        <Text>
          {intl.formatMessage({
            defaultMessage:
              "Finalisation du dossier entre toutes les parties et le représentant  de la compagnie d’assurances. Un accord sur l’indemnité est consigné par un procès-verbal d’expertise et une lettre d’acceptation signée par les deux parties.",
          })}
        </Text>
      ),
    },
    {
      dayLabel: intl.formatMessage({ defaultMessage: "..." }),
      title: intl.formatMessage({
        defaultMessage: "Suivi jusqu’à la fin de la reconstruction",
      }),
      children: (
        <Text whiteSpace="pre-wrap">
          {intl.formatMessage(
            {
              defaultMessage:
                "Jusqu’à la fin de la reconstruction, notre cabinet suit l’évolution des travaux et transmet à la compagnie  d’assurances les factures afin de permettre à l’assuré de percevoir  toutes les indemnités payables en différé.{br}Nota : Sauf imprévu ou cas fortuit et sous réserve d’une expertise judiciaire  engagée par l’assureur, notre cabinet respecte généralement les délais  mentionnés ci-dessus.",
            },
            {
              br: <br />,
            },
          )}
        </Text>
      ),
    },
  ];
}
